import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardHeader from '@/components/Header/DashboardHeader';
import { Footer } from '@/components/organisms/Footer';
import { apiService } from '@/services/api';
import CreateOrderModal from '@/components/Orders/CreateOrderModal/CreateOrderModal';
import OrderStatusBadge from '@/components/Orders/OrderStatusBadge/OrderStatusBadge';
import type { Order } from '@/types/order';
import styles from './OrdersListPage.module.scss';

const POLL_MS = 10000;

export default function OrdersListPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const branchId = searchParams.get('branchId') ?? undefined;

  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(searchParams.get('new') === '1');
  const mounted = useRef(true);

  const load = useCallback(async (showSpinner = false) => {
    if (showSpinner) setLoading(true);
    try {
      const page = await apiService.getMyOrders({ pageSize: 50 });
      if (!mounted.current) return;
      setOrders(page.items ?? []);
      setError(null);
    } catch (e: any) {
      if (mounted.current) setError(e?.message || 'Failed to load orders.');
    } finally {
      if (mounted.current && showSpinner) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    load(true);
    const id = setInterval(() => load(false), POLL_MS);
    return () => {
      mounted.current = false;
      clearInterval(id);
    };
  }, [load]);

  const handleCreated = (order: Order) => {
    setModalOpen(false);
    setOrders((prev) => [order, ...prev.filter((o) => o.id !== order.id)]);
    navigate(`/customer/orders/${order.id}`);
  };

  return (
    <>
      <DashboardHeader />
      <div className={styles.container}>
        <div className={styles.topBar}>
          <button type="button" className={styles.backButton} onClick={() => navigate(-1)}>← Back</button>
          <button type="button" className={styles.createButton} onClick={() => setModalOpen(true)}>+ New order</button>
        </div>
        <h1 className={styles.title}>My orders</h1>

        {loading && <p className={styles.muted}>Loading orders…</p>}
        {error && !loading && <p className={styles.error}>{error}</p>}
        {!loading && !error && orders.length === 0 && (
          <p className={styles.empty}>No orders yet. Tap “New order” to create one — the order number is assigned automatically.</p>
        )}

        {!loading && orders.length > 0 && (
          <ul className={styles.list}>
            {orders.map((order) => {
              const itemCount = (order.items ?? []).length;
              return (
                <li key={order.id} className={styles.card} onClick={() => navigate(`/customer/orders/${order.id}`)}>
                  <div className={styles.cardTop}>
                    <span className={styles.orderNo}>#{order.orderNumber}</span>
                    <OrderStatusBadge status={order.status} />
                  </div>
                  <div className={styles.cardBody}>
                    <span className={styles.itemCount}>{itemCount} item{itemCount !== 1 ? 's' : ''}</span>
                    <span className={styles.date}>
                      {new Date(order.createdAt).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <Footer />

      <CreateOrderModal
        open={modalOpen}
        defaultBranchId={branchId}
        onClose={() => setModalOpen(false)}
        onCreated={handleCreated}
      />
    </>
  );
}
